import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, ArrowLeft, ArrowRight, Home, Car, Briefcase, Building2, Users,
  FileText, Scale, Heart, DollarSign, Lightbulb, Monitor, MapPin,
  Search, Clock, CheckCircle, Star, Loader2, ChevronRight, Info,
  Gavel, Sparkles, AlertTriangle, Check, Copy, Filter, ChevronDown
} from 'lucide-react';

// Category configuration
const CATEGORIES = {
  arriendos: { name: "Arriendos", icon: Home, color: "blue", gradient: "from-blue-500 to-blue-600" },
  compraventas: { name: "Compraventas", icon: Car, color: "emerald", gradient: "from-emerald-500 to-emerald-600" },
  laborales: { name: "Laborales", icon: Briefcase, color: "amber", gradient: "from-amber-500 to-amber-600" },
  comerciales: { name: "Comerciales", icon: Building2, color: "purple", gradient: "from-purple-500 to-purple-600" },
  civiles: { name: "Civiles", icon: Scale, color: "slate", gradient: "from-slate-500 to-slate-600" },
  familia: { name: "Familia", icon: Heart, color: "rose", gradient: "from-rose-500 to-rose-600" },
  financieros: { name: "Financieros", icon: DollarSign, color: "green", gradient: "from-green-500 to-green-600" },
  propiedad_intelectual: { name: "Propiedad Intelectual", icon: Lightbulb, color: "yellow", gradient: "from-yellow-500 to-yellow-600" },
  tecnologia: { name: "Tecnología", icon: Monitor, color: "violet", gradient: "from-violet-500 to-violet-600" },
  inmobiliarios: { name: "Inmobiliarios", icon: MapPin, color: "indigo", gradient: "from-indigo-500 to-indigo-600" }
};

const COMPLEXITY_LABELS = {
  simple: { label: "Simple", color: "emerald", description: "15-20 min" },
  medium: { label: "Intermedio", color: "amber", description: "25-35 min" },
  complex: { label: "Complejo", color: "red", description: "40-60 min" }
};

const ROLE_ICONS = {
  arrendador: "🏠", arrendatario: "🔑",
  vendedor: "💰", comprador: "🛒",
  empleador: "🏢", trabajador: "👤",
  mandante: "📋", mandatario: "✍️",
  acreedor: "💵", deudor: "📝",
  default: "👤"
};

const IMPORTANCE_COLORS = {
  critica: { bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/30", label: "Crítica" },
  alta: { bg: "bg-orange-500/20", text: "text-orange-400", border: "border-orange-500/30", label: "Alta" },
  media: { bg: "bg-yellow-500/20", text: "text-yellow-400", border: "border-yellow-500/30", label: "Media" },
  baja: { bg: "bg-blue-500/20", text: "text-blue-400", border: "border-blue-500/30", label: "Baja" }
};

const SmartContractCreationPage = () => {
  const { templateId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();

  // Steps: 1-Select Contract, 2-Select Role, 3-View Clauses, 4-Generate
  const [currentStep, setCurrentStep] = useState(templateId ? 2 : 1);
  const [loading, setLoading] = useState(false);

  // Step 1: Contract Selection
  const [contracts, setContracts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContract, setSelectedContract] = useState(null);

  // Step 2: Role Selection
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);

  // Step 3: Clauses
  const [protectiveClauses, setProtectiveClauses] = useState([]);
  const [exceptionClauses, setExceptionClauses] = useState([]);
  const [selectedClauses, setSelectedClauses] = useState([]);
  const [expandedClause, setExpandedClause] = useState(null);
  const [counterpartyClauses, setCounterpartyClauses] = useState([]);
  const [showNegotiationTips, setShowNegotiationTips] = useState(false);

  // Step 4: Generation
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchContracts();
  }, []);

  useEffect(() => {
    if (templateId) {
      fetchContractDetails(templateId);
    }
  }, [templateId]);

  const fetchContracts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/chilean-library/contracts');
      setContracts(response.data.contracts || []);
      
      const catResponse = await api.get('/chilean-library/categories');
      setCategories(catResponse.data.categories || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast.error('Error al cargar contratos');
    } finally {
      setLoading(false);
    }
  };

  const fetchContractDetails = async (contractId) => {
    setLoading(true);
    try {
      const response = await api.get(`/chilean-library/contracts/${contractId}`);
      setSelectedContract(response.data.contract);
      setAvailableRoles(response.data.available_roles || response.data.contract?.roles || []);
      setCurrentStep(2);
    } catch (error) {
      console.error('Error fetching contract details:', error);
      // Fallback to local data
      const contract = contracts.find(c => c.id === contractId);
      if (contract) {
        setSelectedContract(contract);
        setAvailableRoles(contract.roles || []);
        setCurrentStep(2);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchClausesForRole = async () => {
    if (!selectedContract || !selectedRole) return;

    setLoading(true);
    try {
      // Get protective clauses
      const clauseResponse = await api.get(
        `/chilean-library/clauses/${selectedContract.id}?user_role=${selectedRole}`
      );
      setProtectiveClauses(clauseResponse.data.protective_clauses || []);
      setExceptionClauses(clauseResponse.data.exception_clauses || []);

      // Get counterparty clauses for negotiation
      const negResponse = await api.get(
        `/chilean-library/negotiation/${selectedContract.id}?current_role=${selectedRole}`
      );
      setCounterpartyClauses(negResponse.data.enriched_clauses || []);

      setCurrentStep(3);
    } catch (error) {
      console.error('Error fetching clauses:', error);
      toast.error('Error al cargar cláusulas');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectContract = (contract) => {
    setSelectedContract(contract);
    setAvailableRoles(contract.roles || []);
    setSelectedRole(null);
    setSelectedClauses([]);
    setCurrentStep(2);
  };

  const handleSelectRole = (role) => {
    setSelectedRole(role);
    fetchClausesForRole();
  };

  const handleToggleClause = (clause) => {
    const isSelected = selectedClauses.some(c => c.id === clause.id);
    if (isSelected) {
      setSelectedClauses(selectedClauses.filter(c => c.id !== clause.id));
    } else {
      setSelectedClauses([...selectedClauses, clause]);
    }
  };

  const handleCopyClause = (clause) => {
    navigator.clipboard.writeText(clause.content);
    toast.success('Cláusula copiada');
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      // Navigate to discovery with selected clauses
      const queryParams = new URLSearchParams({
        role: selectedRole,
        clauses: selectedClauses.map(c => c.id).join(',')
      });
      navigate(`/discovery/${selectedContract.id}?${queryParams}`);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al iniciar generación');
      setGenerating(false);
    }
  };

  const filteredContracts = contracts.filter(contract => {
    const matchesCategory = selectedCategory === 'all' || contract.category === selectedCategory;
    const matchesSearch = !searchQuery || 
      contract.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contract.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryInfo = (catId) => CATEGORIES[catId] || { name: catId, icon: FileText, color: "slate" };

  const getRoleLabel = (role) => {
    const labels = {
      arrendador: "Arrendador (Dueño)",
      arrendatario: "Arrendatario (Inquilino)",
      vendedor: "Vendedor",
      comprador: "Comprador",
      empleador: "Empleador",
      trabajador: "Trabajador",
      mandante: "Mandante",
      mandatario: "Mandatario"
    };
    return labels[role] || role.charAt(0).toUpperCase() + role.slice(1);
  };

  return (
    <div className="min-h-screen mesh-gradient">
      {/* Header */}
      <header className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link 
              to="/dashboard" 
              className="flex items-center space-x-3 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <div className="flex items-center space-x-3">
              <Shield className="w-6 h-6 text-blue-500" />
              <span className="font-semibold font-['Outfit']">Crear Contrato Inteligente</span>
            </div>
            <div className="text-sm text-slate-400">
              Paso {currentStep} de 4
            </div>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-8">
          {['Seleccionar', 'Tu Rol', 'Cláusulas', 'Generar'].map((step, index) => (
            <div key={index} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                currentStep > index + 1 
                  ? 'bg-blue-500 border-blue-500 text-white' 
                  : currentStep === index + 1 
                    ? 'border-blue-500 text-blue-400' 
                    : 'border-slate-600 text-slate-500'
              }`}>
                {currentStep > index + 1 ? <Check className="w-5 h-5" /> : index + 1}
              </div>
              <span className={`ml-2 text-sm hidden sm:inline ${
                currentStep >= index + 1 ? 'text-white' : 'text-slate-500'
              }`}>{step}</span>
              {index < 3 && (
                <div className={`w-12 sm:w-24 h-0.5 mx-2 ${
                  currentStep > index + 1 ? 'bg-blue-500' : 'bg-slate-700'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 pb-12">
        <AnimatePresence mode="wait">
          {/* Step 1: Contract Selection */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="text-center mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] mb-2">
                  Selecciona el Tipo de Contrato
                </h1>
                <p className="text-slate-400">
                  Más de {contracts.length} contratos adaptados a la legislación chilena
                </p>
              </div>

              {/* Search and Filter */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Buscar contratos..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
                    data-testid="search-contracts"
                  />
                </div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  data-testid="category-filter"
                >
                  <option value="all">Todas las Categorías</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name} ({cat.count})
                    </option>
                  ))}
                </select>
              </div>

              {/* Category Pills */}
              <div className="flex flex-wrap gap-2 mb-6">
                {Object.entries(CATEGORIES).slice(0, 6).map(([id, cat]) => (
                  <button
                    key={id}
                    onClick={() => setSelectedCategory(id === selectedCategory ? 'all' : id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                      selectedCategory === id
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700'
                    }`}
                  >
                    <cat.icon className="w-3.5 h-3.5" />
                    {cat.name}
                  </button>
                ))}
              </div>

              {/* Contracts Grid */}
              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-3" />
                  <p className="text-slate-400">Cargando contratos...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredContracts.map((contract, index) => {
                    const category = getCategoryInfo(contract.category);
                    const CategoryIcon = category.icon;
                    const complexity = COMPLEXITY_LABELS[contract.complexity] || COMPLEXITY_LABELS.medium;

                    return (
                      <button
                        key={contract.id}
                        onClick={() => handleSelectContract(contract)}
                        data-testid={`contract-${contract.id}`}
                        className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 text-left hover:border-blue-500/50 hover:bg-slate-800 transition-all group"
                        style={{ animationDelay: `${(index % 12) * 0.03}s` }}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br ${category.gradient}`}>
                            <CategoryIcon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex items-center gap-2">
                            {contract.requires_notary && (
                              <span className="text-xs px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">
                                Notaría
                              </span>
                            )}
                            <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-blue-400 transition-colors" />
                          </div>
                        </div>
                        <h3 className="font-semibold mb-1 group-hover:text-blue-400 transition-colors line-clamp-1">
                          {contract.name}
                        </h3>
                        <p className="text-sm text-slate-400 mb-3 line-clamp-2">{contract.description}</p>
                        <div className="flex items-center justify-between text-xs">
                          <span className={`px-2 py-0.5 rounded bg-${complexity.color}-500/10 text-${complexity.color}-400`}>
                            {complexity.label}
                          </span>
                          <span className="text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {contract.estimated_time}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}

          {/* Step 2: Role Selection */}
          {currentStep === 2 && selectedContract && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="max-w-2xl mx-auto"
            >
              <button
                onClick={() => { setCurrentStep(1); setSelectedContract(null); }}
                className="flex items-center gap-2 text-slate-400 hover:text-white mb-6"
              >
                <ArrowLeft className="w-4 h-4" /> Cambiar contrato
              </button>

              <div className="text-center mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] mb-2">
                  ¿Cuál es tu rol en este contrato?
                </h1>
                <p className="text-slate-400">
                  Te sugeriremos cláusulas que protejan tus intereses según tu posición
                </p>
              </div>

              {/* Selected Contract Info */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-gradient-to-br ${
                    getCategoryInfo(selectedContract.category).gradient
                  }`}>
                    {React.createElement(getCategoryInfo(selectedContract.category).icon, { className: "w-6 h-6 text-white" })}
                  </div>
                  <div>
                    <h3 className="font-semibold">{selectedContract.name}</h3>
                    <p className="text-sm text-slate-400">{selectedContract.legal_basis}</p>
                  </div>
                </div>
              </div>

              {/* Role Options */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {availableRoles.map((role) => (
                  <button
                    key={role}
                    onClick={() => handleSelectRole(role)}
                    data-testid={`role-${role}`}
                    className={`p-6 rounded-xl border-2 transition-all text-left ${
                      selectedRole === role
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-slate-700 hover:border-blue-500/50 bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-4xl">{ROLE_ICONS[role] || ROLE_ICONS.default}</span>
                      <div>
                        <h4 className="font-semibold text-lg">{getRoleLabel(role)}</h4>
                        <p className="text-sm text-slate-400">
                          {role.includes('arrendador') || role.includes('vendedor') || role.includes('empleador')
                            ? 'Recibirás cláusulas de protección y garantía'
                            : 'Recibirás cláusulas de estabilidad y derechos'}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {availableRoles.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <Info className="w-8 h-8 mx-auto mb-3" />
                  <p>Este contrato no tiene roles específicos definidos.</p>
                  <button
                    onClick={() => navigate(`/discovery/${selectedContract.id}`)}
                    className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Continuar sin selección de rol
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 3: Clause Selection */}
          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <button
                onClick={() => { setCurrentStep(2); setSelectedRole(null); }}
                className="flex items-center gap-2 text-slate-400 hover:text-white mb-6"
              >
                <ArrowLeft className="w-4 h-4" /> Cambiar rol
              </button>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: Your Clauses */}
                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-bold flex items-center gap-2">
                        <Shield className="w-5 h-5 text-blue-400" />
                        Cláusulas Recomendadas para Ti
                      </h2>
                      <span className="text-sm text-slate-400">
                        {selectedClauses.length} seleccionadas
                      </span>
                    </div>

                    {loading ? (
                      <div className="text-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {protectiveClauses.map((clause) => {
                          const isSelected = selectedClauses.some(c => c.id === clause.id);
                          const isExpanded = expandedClause === clause.id;
                          const importance = IMPORTANCE_COLORS[clause.importance] || IMPORTANCE_COLORS.media;

                          return (
                            <div
                              key={clause.id}
                              className={`bg-slate-800/50 rounded-xl border transition-all ${
                                isSelected ? 'border-blue-500' : 'border-slate-700'
                              }`}
                            >
                              <div
                                className="p-4 cursor-pointer"
                                onClick={() => setExpandedClause(isExpanded ? null : clause.id)}
                              >
                                <div className="flex items-start gap-3">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleToggleClause(clause); }}
                                    className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                      isSelected ? 'bg-blue-500 border-blue-500' : 'border-slate-500 hover:border-blue-400'
                                    }`}
                                    data-testid={`clause-checkbox-${clause.id}`}
                                  >
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                  </button>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${importance.bg} ${importance.text}`}>
                                        {importance.label}
                                      </span>
                                      <span className="text-xs text-slate-500">{clause.type}</span>
                                    </div>
                                    <h4 className="font-medium">{clause.name}</h4>
                                    <p className="text-sm text-slate-400 mt-1">{clause.reason}</p>
                                  </div>
                                  <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </div>
                              </div>

                              <AnimatePresence>
                                {isExpanded && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="border-t border-slate-700"
                                  >
                                    <div className="p-4 bg-slate-900/50">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-slate-400">Texto de la cláusula:</span>
                                        <button
                                          onClick={() => handleCopyClause(clause)}
                                          className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                                        >
                                          <Copy className="w-3 h-3" /> Copiar
                                        </button>
                                      </div>
                                      <div className="bg-slate-800 rounded-lg p-3 text-sm text-slate-300 font-mono whitespace-pre-wrap max-h-48 overflow-y-auto">
                                        {clause.content}
                                      </div>
                                      {clause.legal_basis && (
                                        <p className="mt-2 text-xs text-slate-500 flex items-center gap-1">
                                          <Scale className="w-3 h-3" /> {clause.legal_basis}
                                        </p>
                                      )}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Exception Clauses */}
                    {exceptionClauses.length > 0 && (
                      <div className="mt-8">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-400" />
                          Cláusulas de Excepción
                        </h3>
                        <div className="space-y-3">
                          {exceptionClauses.map((clause) => (
                            <div
                              key={clause.id}
                              className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4"
                            >
                              <h4 className="font-medium text-amber-300">{clause.name}</h4>
                              <p className="text-sm text-amber-200/70 mt-1">{clause.applies_when}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: Negotiation Tips */}
                <div className="space-y-6">
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-400" />
                      Asistente de Negociación
                    </h3>
                    <p className="text-sm text-slate-400 mb-4">
                      La contraparte ({getRoleLabel(availableRoles.find(r => r !== selectedRole) || '')}) 
                      típicamente solicita estas cláusulas:
                    </p>
                    
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {counterpartyClauses.slice(0, 5).map((clause) => (
                        <div key={clause.id} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              clause.risk_level === 'alto' ? 'bg-red-500/20 text-red-400' :
                              clause.risk_level === 'medio-alto' ? 'bg-orange-500/20 text-orange-400' :
                              'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              Riesgo: {clause.risk_level}
                            </span>
                          </div>
                          <h5 className="text-sm font-medium">{clause.name}</h5>
                          <p className="text-xs text-slate-400 mt-1">{clause.likely_reason}</p>
                        </div>
                      ))}
                    </div>

                    {counterpartyClauses.length > 5 && (
                      <button
                        onClick={() => setShowNegotiationTips(!showNegotiationTips)}
                        className="mt-4 w-full text-sm text-blue-400 hover:text-blue-300"
                      >
                        Ver {counterpartyClauses.length - 5} más
                      </button>
                    )}
                  </div>

                  {/* Quick Tips */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5">
                    <h4 className="font-medium text-blue-300 mb-3">Tips de Negociación</h4>
                    <ul className="space-y-2 text-sm text-blue-200/80">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        Priorice las cláusulas marcadas como "Críticas"
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        Prepare alternativas para puntos de desacuerdo
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        Documente todos los acuerdos por escrito
                      </li>
                    </ul>
                  </div>

                  {/* Generate Button */}
                  <button
                    onClick={handleGenerate}
                    disabled={generating}
                    className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="generate-contract-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Preparando...
                      </>
                    ) : (
                      <>
                        <ArrowRight className="w-5 h-5" />
                        Continuar a Generación
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default SmartContractCreationPage;
